import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowUpLeft, X } from 'lucide-react';

/**
 * BreadcrumbFromOrigin — a lightweight "Coming from ..." pill that appears at
 * the top of a target page when the user arrives via a chain-navigation link
 * that carries `?from=<origin_number>` (e.g. MR-2026-0001, PR-2026-0004).
 *
 * • Reads the `from` query param.
 * • Renders a dismissible pill with a back-arrow link to `sourceHref`.
 * • Silently renders nothing when `from` is absent.
 *
 * Usage: <BreadcrumbFromOrigin sourceLabel="Material Request" sourceHref="/material-requests" />
 */
export default function BreadcrumbFromOrigin({ sourceLabel, sourceHref }) {
  const [params, setParams] = useSearchParams();
  const from = params.get('from');
  const [hidden, setHidden] = React.useState(false);

  if (!from || hidden) return null;

  const dismiss = () => {
    setHidden(true);
    const next = new URLSearchParams(params);
    next.delete('from');
    setParams(next, { replace: true });
  };

  return (
    <div
      data-testid="breadcrumb-from-origin"
      className="inline-flex items-center gap-2 rounded-full border border-amber-400 bg-amber-50 px-3 py-1.5 text-xs shadow-sm animate-in fade-in slide-in-from-top-2"
    >
      <a href={sourceHref} className="flex items-center gap-1.5 text-amber-800 hover:text-amber-900 font-semibold" title={`Back to ${sourceLabel}`}>
        <ArrowUpLeft className="w-3.5 h-3.5" />
        Coming from {sourceLabel} <span className="font-mono">{from}</span>
      </a>
      <button
        type="button"
        onClick={dismiss}
        className="ml-1 rounded-full p-0.5 hover:bg-amber-100"
        aria-label="Dismiss breadcrumb"
        data-testid="breadcrumb-dismiss"
      >
        <X className="w-3 h-3 text-amber-700" />
      </button>
    </div>
  );
}
